import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  ArrowLeft, Users, BookOpen, FileText, MessageSquare, Send,
  Pin, Paperclip, Trash2, Edit, Loader2,
  FolderOpen, Plus, ChevronDown, ChevronRight, Copy, Check,
  Briefcase, Link2, ThumbsUp, Heart, Lightbulb, CheckCircle2,
  ClipboardList, HelpCircle, Video, Bell, BellOff, CornerDownRight, X,
  Upload, UserPlus, Search, BarChart3, Settings, Download,
  BarChart2, Lock, Eye
} from 'lucide-react';
import axios from 'axios';
import CampusTareas from './CampusTareas';
import CampusQuizzes from './CampusQuizzes';
import CampusMeetings from './CampusMeetings';
import CampusNotifConfig from './CampusNotifConfig';
import CampusCursoVinculadoConfig from '../../../components/campus/CampusCursoVinculadoConfig';
import MiEstadoCurso from '../../../components/campus/MiEstadoCurso';
import { UserHeader } from '../../../components/UserHeader';
import MentionInput from '../../../components/campus/MentionInput';
import CampusPostCard from '../../../components/campus/CampusPostCard';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Reacciones para POSTS — sincronizado con ReactionPicker.jsx (6 tipos Facebook-like)
const REACTION_CONFIG = [
  { tipo: 'like',      emoji: '👍', label: 'Me gusta' },
  { tipo: 'love',      emoji: '❤️', label: 'Me encanta' },
  { tipo: 'interesa',  emoji: '💡', label: 'Me interesa' },
  { tipo: 'sorprende', emoji: '😮', label: 'Me sorprende' },
  { tipo: 'triste',    emoji: '😢', label: 'Me entristece' },
  { tipo: 'enoja',     emoji: '😠', label: 'Me enoja' },
];

export default function CampusEspacioPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  const [espacio, setEspacio] = useState(null);
  const [activeTab, setActiveTab] = useState('stream');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Stream state
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({
    titulo: '', contenido: '', tipo: 'anuncio',
    encuesta: { opciones: ['', ''], multi: false, anonima: true, cierre: '' }
  });
  const [savingPost, setSavingPost] = useState(false);
  // Edición de post NO-encuesta (anuncio, material, etc.)
  const [editingPostId, setEditingPostId] = useState(null);
  const [votingPost, setVotingPost] = useState({}); // { postId: bool }
  const [pollSelection, setPollSelection] = useState({}); // { postId: [opcionId,...] }
  const [showVoters, setShowVoters] = useState({}); // { postId+opcionId: bool }
  // Edición de encuesta existente
  const [editingPoll, setEditingPoll] = useState(null); // postId que se está editando
  const [editPollForm, setEditPollForm] = useState({ titulo: '', opciones: [], multi: false, cierre: '' });
  const [savingEditPoll, setSavingEditPoll] = useState(false);

  // Comments state (per post)
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsData, setCommentsData] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [commentMenciones, setCommentMenciones] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  const [replyingTo, setReplyingTo] = useState({}); // {postId: {commentId, autorNombre}}

  // Reactions state
  const [reactingPost, setReactingPost] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(null);

  // Members state
  const [miembros, setMiembros] = useState([]);
  const [loadingMiembros, setLoadingMiembros] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [miembrosQuery, setMiembrosQuery] = useState('');

  // Topics & Materials state
  const [temas, setTemas] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [loadingMateriales, setLoadingMateriales] = useState(false);
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [topicForm, setTopicForm] = useState({ nombre: '', descripcion: '' });
  const [expandedTopics, setExpandedTopics] = useState({});
  const [showMaterialForm, setShowMaterialForm] = useState(null);
  const [materialForm, setMaterialForm] = useState({ titulo: '', descripcion: '', tipo: 'link', url_externa: '' });

  // Notification preferences
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [togglingNotif, setTogglingNotif] = useState(false);

  // Add member search
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingUser, setAddingUser] = useState(null);
  const [changingRole, setChangingRole] = useState(null); // user_id of member being edited

  // Invite by email
  const [showInviteEmail, setShowInviteEmail] = useState(false);
  const [inviteMode, setInviteMode] = useState('single'); // 'single' | 'bulk'
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBulkText, setInviteBulkText] = useState('');
  const [inviteRol, setInviteRol] = useState('estudiante');
  const [inviteMsg, setInviteMsg] = useState('');
  const [invitingEmail, setInvitingEmail] = useState(false);
  const [inviteFeedback, setInviteFeedback] = useState(null); // { ok, message }
  const [bulkResults, setBulkResults] = useState(null);
  const [invitacionesPendientes, setInvitacionesPendientes] = useState([]);
  const [loadingInvitaciones, setLoadingInvitaciones] = useState(false);
  const [reenviandoInv, setReenviandoInv] = useState(null);
  const [cancelandoInv, setCancelandoInv] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const esAdminGlobal = ['admin', 'superadmin', 'administracion'].includes(user?.rol);
  const esCoordinadorODocente = espacio?.mi_rol === 'coordinador' || espacio?.mi_rol === 'docente' || esAdminGlobal;

  const fetchEspacio = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}`, { headers });
      setEspacio(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar el espacio');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/posts`, { headers });
      setPosts(res.data.posts || []);
    } catch (err) {
      console.error('Error cargando posts:', err);
    } finally {
      setLoadingPosts(false);
    }
  }, [spaceId]);

  const fetchMiembros = useCallback(async () => {
    setLoadingMiembros(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/miembros`, { headers });
      setMiembros(res.data);
    } catch (err) {
      console.error('Error cargando miembros:', err);
    } finally {
      setLoadingMiembros(false);
    }
  }, [spaceId]);

  const fetchNotifPrefs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/notificaciones/preferencias`, { headers });
      setNotifEnabled(!res.data.email_desactivado);
    } catch (err) {
      console.error('Error cargando prefs:', err);
    }
  }, [spaceId]);

  const toggleNotif = async () => {
    setTogglingNotif(true);
    try {
      const nuevoEstado = notifEnabled;
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/notificaciones/preferencias?email_desactivado=${nuevoEstado}`,
        {}, { headers }
      );
      setNotifEnabled(!nuevoEstado);
    } catch (err) {
      console.error('Error toggling notifs:', err);
    } finally {
      setTogglingNotif(false);
    }
  };

  const buscarUsuarios = async (q) => {
    setSearchQuery(q);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(
        `${API_URL}/campus/espacios/${spaceId}/buscar-usuarios?q=${encodeURIComponent(q.trim())}`,
        { headers }
      );
      setSearchResults(res.data);
    } catch (err) {
      console.error('Error buscando usuarios:', err);
    } finally {
      setSearching(false);
    }
  };

  const agregarMiembro = async (usuario) => {
    setAddingUser(usuario.user_id);
    try {
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/miembros`,
        { user_id: usuario.user_id, user_type: usuario.user_type, rol: 'estudiante' },
        { headers }
      );
      setSearchResults(prev => prev.filter(u => u.user_id !== usuario.user_id));
      fetchMiembros();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al agregar miembro');
    } finally {
      setAddingUser(null);
    }
  };

  const cambiarRol = async (userId, nuevoRol) => {
    try {
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/miembros/${userId}/rol?rol=${nuevoRol}`,
        {}, { headers }
      );
      setChangingRole(null);
      fetchMiembros();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  const removerMiembro = async (userId, nombre) => {
    if (!window.confirm(`¿Remover a ${nombre} del espacio?`)) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/miembros/${userId}`, { headers });
      fetchMiembros();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al remover');
    }
  };

  const exportarEmailsMiembros = (lista) => {
    const conEmail = (lista || []).filter((m) => (m.email || '').trim());
    if (conEmail.length === 0) {
      alert('No hay miembros con email para exportar.');
      return;
    }
    const slug = (espacio?.nombre || 'espacio')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    const fecha = new Date().toISOString().slice(0, 10);

    const escapar = (val) => {
      const s = String(val ?? '');
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const filas = [
      ['nombre', 'apellido', 'email', 'rol'],
      ...conEmail.map((m) => [m.nombre || '', m.apellido || '', m.email, m.rol || 'estudiante']),
    ];
    const csv = '\ufeff' + filas.map((r) => r.map(escapar).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campus_${slug}_emails_${fecha}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchInvitacionesPendientes = useCallback(async () => {
    setLoadingInvitaciones(true);
    try {
      const res = await axios.get(
        `${API_URL}/campus/espacios/${spaceId}/invitaciones-pendientes`,
        { headers }
      );
      setInvitacionesPendientes(res.data || []);
    } catch (err) {
      console.error('Error cargando invitaciones:', err);
    } finally {
      setLoadingInvitaciones(false);
    }
  }, [spaceId]);

  const enviarInvitacionEmail = async () => {
    const email = inviteEmail.trim();
    if (!email) {
      setInviteFeedback({ ok: false, message: 'Ingresá un email válido.' });
      return;
    }
    setInvitingEmail(true);
    setInviteFeedback(null);
    try {
      const res = await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/invitar-email`,
        { email, rol: inviteRol, mensaje_personalizado: inviteMsg.trim() || null },
        { headers }
      );
      // Si el tipo termina con '_sin_email', el envío falló pero la acción se completó
      const enviarFallo = res.data?.tipo?.endsWith('_sin_email');
      setInviteFeedback({
        ok: !enviarFallo,
        message: res.data.message,
        warning: enviarFallo,
      });
      if (!enviarFallo) {
        setInviteEmail('');
        setInviteMsg('');
      }
      // Refrescar listas
      fetchInvitacionesPendientes();
      if (res.data.tipo === 'miembro_existente' || res.data.tipo === 'miembro_existente_sin_email') fetchMiembros();
    } catch (err) {
      setInviteFeedback({ ok: false, message: err.response?.data?.detail || 'No pudimos enviar la invitación.' });
    } finally {
      setInvitingEmail(false);
    }
  };

  const enviarInvitacionMasiva = async () => {
    // Parsear emails: separar por coma, espacio, salto de línea o ;
    const raw = inviteBulkText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    // Dedupe + validación básica de formato email
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validos = [];
    const invalidos = [];
    const vistos = new Set();
    for (const e of raw) {
      const lower = e.toLowerCase();
      if (vistos.has(lower)) continue;
      vistos.add(lower);
      if (regex.test(lower)) validos.push(lower);
      else invalidos.push(e);
    }
    if (validos.length === 0) {
      setInviteFeedback({ ok: false, message: 'No encontré ningún email válido. Separá con coma, espacio o salto de línea.' });
      return;
    }
    if (validos.length > 100) {
      setInviteFeedback({ ok: false, message: 'Máximo 100 emails por carga. Dividí en lotes más chicos.' });
      return;
    }

    setInvitingEmail(true);
    setInviteFeedback(null);
    setBulkResults(null);
    try {
      const res = await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/invitar-emails-masivo`,
        { emails: validos, rol: inviteRol, mensaje_personalizado: inviteMsg.trim() || null },
        { headers }
      );
      const { total, exitosas, fallidas, resultados } = res.data;
      setBulkResults({ total, exitosas, fallidas, resultados, invalidos });
      setInviteFeedback({
        ok: fallidas === 0,
        warning: fallidas > 0 && exitosas > 0,
        message:
          fallidas === 0
            ? `Procesamos los ${total} emails correctamente.`
            : exitosas === 0
              ? `No pudimos procesar ninguno de los ${total} emails.`
              : `Procesamos ${exitosas} de ${total}. ${fallidas} fallaron (ver detalle).`,
      });
      // Refrescar listas
      fetchInvitacionesPendientes();
      fetchMiembros();
      if (exitosas === total) setInviteBulkText('');
    } catch (err) {
      setInviteFeedback({ ok: false, message: err.response?.data?.detail || 'No pudimos procesar la carga.' });
    } finally {
      setInvitingEmail(false);
    }
  };

  const reenviarInvitacion = async (invitacionId) => {
    setReenviandoInv(invitacionId);
    try {
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/invitaciones-pendientes/${invitacionId}/reenviar`,
        {},
        { headers }
      );
      fetchInvitacionesPendientes();
      setInviteFeedback({ ok: true, message: 'Invitación reenviada.' });
    } catch (err) {
      setInviteFeedback({ ok: false, message: err.response?.data?.detail || 'No pudimos reenviar.' });
    } finally {
      setReenviandoInv(null);
    }
  };

  const cancelarInvitacion = async (invitacionId, email) => {
    if (!window.confirm(`¿Cancelar la invitación a ${email}?`)) return;
    setCancelandoInv(invitacionId);
    try {
      await axios.delete(
        `${API_URL}/campus/espacios/${spaceId}/invitaciones-pendientes/${invitacionId}`,
        { headers }
      );
      fetchInvitacionesPendientes();
    } catch (err) {
      alert(err.response?.data?.detail || 'No pudimos cancelar.');
    } finally {
      setCancelandoInv(null);
    }
  };

  const fetchTemas = useCallback(async () => {
    setLoadingMateriales(true);
    try {
      const [temasRes, matsRes] = await Promise.all([
        axios.get(`${API_URL}/campus/espacios/${spaceId}/temas`, { headers }),
        axios.get(`${API_URL}/campus/espacios/${spaceId}/materiales`, { headers })
      ]);
      setTemas(temasRes.data);
      setMateriales(matsRes.data);
    } catch (err) {
      console.error('Error cargando temas/materiales:', err);
    } finally {
      setLoadingMateriales(false);
    }
  }, [spaceId]);

  useEffect(() => { fetchEspacio(); }, [fetchEspacio]);

  // Tracking: registrar ingreso al espacio (una vez por mount)
  useEffect(() => {
    if (!spaceId) return;
    axios.post(
      `${API_URL}/campus/actividad/track`,
      { space_id: spaceId, tipo: 'ingreso_espacio' },
      { headers }
    ).catch(() => { /* silent */ });
  }, [spaceId]);

  useEffect(() => {
    if (!espacio) return;
    if (activeTab === 'stream') fetchPosts();
    else if (activeTab === 'miembros') { fetchMiembros(); fetchNotifPrefs(); fetchInvitacionesPendientes(); }
    else if (activeTab === 'materiales') fetchTemas();
  }, [activeTab, espacio]);

  // Si la URL tiene ?post=xxx (vienen desde una notificación), forzar tab stream
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetPost = params.get('post');
    if (targetPost && activeTab !== 'stream') {
      setActiveTab('stream');
    }
  }, [location.search]);

  // Scroll al post indicado en ?post=xxx una vez que los posts estén renderizados
  useEffect(() => {
    if (!posts || posts.length === 0) return;
    const params = new URLSearchParams(location.search);
    const targetPost = params.get('post');
    if (!targetPost) return;
    // Esperar a que el DOM tenga el nodo
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="post-${targetPost}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-violet-400', 'ring-offset-2');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-violet-400', 'ring-offset-2');
        }, 2500);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [posts, location.search]);

  // --- Post Actions ---
  const resetPostForm = () => setPostForm({
    titulo: '', contenido: '', tipo: 'anuncio',
    encuesta: { opciones: ['', ''], multi: false, anonima: true, cierre: '' }
  });

  const crearPost = async () => {
    if (!postForm.titulo.trim()) return;
    
    // Para encuestas, contenido es opcional pero debe haber al menos 2 opciones
    if (postForm.tipo === 'encuesta') {
      const opcionesLimpias = (postForm.encuesta?.opciones || []).map(o => o.trim()).filter(Boolean);
      if (opcionesLimpias.length < 2) {
        alert('La encuesta debe tener al menos 2 opciones con texto');
        return;
      }
    } else if (!postForm.contenido.trim()) {
      return;
    }
    
    setSavingPost(true);
    try {
      const payload = {
        titulo: postForm.titulo,
        contenido: postForm.contenido,
        tipo: postForm.tipo,
      };
      if (postForm.tipo === 'encuesta') {
        const enc = postForm.encuesta || {};
        payload.encuesta = {
          opciones: (enc.opciones || []).map(t => ({ texto: (t || '').trim() })).filter(o => o.texto),
          multi: !!enc.multi,
          anonima: !!enc.anonima,
          cierre: enc.cierre ? new Date(enc.cierre).toISOString() : null,
        };
      }
      await axios.post(`${API_URL}/campus/espacios/${spaceId}/posts`, payload, { headers });
      setShowPostForm(false);
      resetPostForm();
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear publicación');
    } finally {
      setSavingPost(false);
    }
  };

  // --- Encuesta actions ---
  const togglePollSelection = (post, opcionId) => {
    const enc = post.encuesta || {};
    const multi = !!enc.multi;
    setPollSelection(prev => {
      const current = prev[post.id] || post.encuesta_resultados?.mi_voto || [];
      let nuevo;
      if (multi) {
        nuevo = current.includes(opcionId)
          ? current.filter(id => id !== opcionId)
          : [...current, opcionId];
      } else {
        nuevo = [opcionId];
      }
      return { ...prev, [post.id]: nuevo };
    });
  };

  const votarEncuesta = async (postId) => {
    const seleccion = pollSelection[postId] || [];
    if (seleccion.length === 0) {
      alert('Elegí al menos una opción para votar');
      return;
    }
    setVotingPost(prev => ({ ...prev, [postId]: true }));
    try {
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/posts/${postId}/votar`,
        { opciones: seleccion },
        { headers }
      );
      // Limpiar la selección temporal y refrescar
      setPollSelection(prev => {
        const n = { ...prev };
        delete n[postId];
        return n;
      });
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al votar');
    } finally {
      setVotingPost(prev => ({ ...prev, [postId]: false }));
    }
  };

  const cerrarEncuesta = async (postId, cerrada) => {
    const accion = cerrada ? 'reabrir' : 'cerrar';
    if (!window.confirm(`¿${accion === 'cerrar' ? 'Cerrar' : 'Reabrir'} esta encuesta?`)) return;
    try {
      const endpoint = cerrada ? 'reabrir-encuesta' : 'cerrar-encuesta';
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/posts/${postId}/${endpoint}`,
        {},
        { headers }
      );
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || `Error al ${accion} la encuesta`);
    }
  };

  const abrirEdicionEncuesta = (post) => {
    const enc = post.encuesta || {};
    setEditingPoll(post.id);
    setEditPollForm({
      titulo: post.titulo || '',
      // Conservar el id para no perder los votos al guardar
      opciones: (enc.opciones || []).map(o => ({ id: o.id, texto: o.texto })),
      multi: !!enc.multi,
      cierre: enc.cierre ? enc.cierre.slice(0, 16) : '', // datetime-local format
    });
  };

  const guardarEdicionEncuesta = async () => {
    if (!editingPoll) return;
    const opciones = editPollForm.opciones.map(o => ({ ...o, texto: (o.texto || '').trim() })).filter(o => o.texto);
    if (opciones.length < 2) {
      alert('La encuesta debe tener al menos 2 opciones');
      return;
    }
    if (!editPollForm.titulo.trim()) {
      alert('La consigna no puede estar vacía');
      return;
    }
    setSavingEditPoll(true);
    try {
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/posts/${editingPoll}`,
        {
          titulo: editPollForm.titulo,
          encuesta: {
            opciones,
            multi: editPollForm.multi,
            anonima: false,
            cierre: editPollForm.cierre ? new Date(editPollForm.cierre).toISOString() : null,
          }
        },
        { headers }
      );
      setEditingPoll(null);
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar la encuesta');
    } finally {
      setSavingEditPoll(false);
    }
  };

  const eliminarPost = async (postId) => {
    if (!window.confirm('¿Eliminar esta publicación?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/posts/${postId}`, { headers });
      fetchPosts();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  // --- Editar post (no encuesta) ---
  const iniciarEdicionPost = (post) => {
    setEditingPostId(post.id);
    setPostForm({
      titulo: post.titulo || '',
      contenido: post.contenido || '',
      tipo: post.tipo || 'anuncio',
      encuesta: { opciones: ['', ''], multi: false, anonima: true, cierre: '' },
    });
    setShowPostForm(true);
    // Scroll al form
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const cancelarEdicionPost = () => {
    setEditingPostId(null);
    setShowPostForm(false);
    resetPostForm();
  };

  const actualizarPost = async () => {
    if (!editingPostId) return;
    if (!postForm.titulo.trim()) return;
    if (postForm.tipo !== 'encuesta' && !postForm.contenido.trim()) return;

    setSavingPost(true);
    try {
      const payload = {
        titulo: postForm.titulo,
        contenido: postForm.contenido,
      };
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/posts/${editingPostId}`,
        payload,
        { headers }
      );
      cancelarEdicionPost();
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al actualizar la publicación');
    } finally {
      setSavingPost(false);
    }
  };

  // --- Comment Actions ---
  const fetchComments = async (postId) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await axios.get(
        `${API_URL}/campus/espacios/${spaceId}/posts/${postId}/comentarios`,
        { headers }
      );
      setCommentsData(prev => ({ ...prev, [postId]: res.data }));
    } catch (err) {
      console.error('Error cargando comentarios:', err);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = (postId) => {
    const isExpanded = expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
    if (!isExpanded && !commentsData[postId]) {
      fetchComments(postId);
    }
  };

  const enviarComentario = async (postId, opts = {}) => {
    // opts puede traer: contenido, respuesta_a, menciones — usado desde ComentarioItem (responder a hijo).
    // Si no se pasan, se usan los inputs del formulario principal.
    let texto, menciones, respuesta_a;
    if (opts.contenido !== undefined) {
      texto = (opts.contenido || '').trim();
      menciones = opts.menciones || [];
      respuesta_a = opts.respuesta_a || null;
    } else {
      texto = (commentInputs[postId] || '').trim();
      menciones = commentMenciones[postId] || [];
      respuesta_a = replyingTo[postId]?.commentId || null;
    }
    if (!texto) return;
    setSendingComment(prev => ({ ...prev, [postId]: true }));
    try {
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/posts/${postId}/comentarios`,
        { contenido: texto, respuesta_a, menciones },
        { headers }
      );
      if (opts.contenido === undefined) {
        setCommentInputs(prev => ({ ...prev, [postId]: '' }));
        setCommentMenciones(prev => ({ ...prev, [postId]: [] }));
        setReplyingTo(prev => ({ ...prev, [postId]: null }));
      }
      fetchComments(postId);
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, total_comentarios: (p.total_comentarios || 0) + 1 } : p
      ));
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al comentar');
    } finally {
      setSendingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  const editarComentario = async (postId, commentId, payload) => {
    try {
      await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/comentarios/${commentId}`,
        payload,
        { headers }
      );
      fetchComments(postId);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al editar comentario');
    }
  };

  const reaccionarComentario = async (postId, commentId, tipo) => {
    try {
      await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/comentarios/${commentId}/reacciones`,
        { tipo },
        { headers }
      );
      fetchComments(postId);
    } catch (err) {
      // silent
    }
  };

  const eliminarComentario = async (postId, commentId) => {
    if (!window.confirm('¿Eliminar este comentario?')) return;
    try {
      await axios.delete(
        `${API_URL}/campus/espacios/${spaceId}/comentarios/${commentId}`,
        { headers }
      );
      fetchComments(postId);
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, total_comentarios: Math.max(0, (p.total_comentarios || 1) - 1) } : p
      ));
    } catch (err) {
      alert('Error al eliminar comentario');
    }
  };

  // --- Reaction Actions ---
  const toggleReaction = async (postId, tipo) => {
    setReactingPost(postId);
    setShowReactionPicker(null);
    try {
      const res = await axios.post(
        `${API_URL}/campus/espacios/${spaceId}/posts/${postId}/reacciones`,
        { tipo },
        { headers }
      );
      const { action } = res.data;
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const reacciones = { ...p.reacciones };
        const oldReaction = p.mi_reaccion;

        if (action === 'removed') {
          reacciones[tipo] = Math.max(0, (reacciones[tipo] || 1) - 1);
          if (reacciones[tipo] === 0) delete reacciones[tipo];
          return { ...p, reacciones, mi_reaccion: null };
        } else if (action === 'changed') {
          if (oldReaction) {
            reacciones[oldReaction] = Math.max(0, (reacciones[oldReaction] || 1) - 1);
            if (reacciones[oldReaction] === 0) delete reacciones[oldReaction];
          }
          reacciones[tipo] = (reacciones[tipo] || 0) + 1;
          return { ...p, reacciones, mi_reaccion: tipo };
        } else {
          reacciones[tipo] = (reacciones[tipo] || 0) + 1;
          return { ...p, reacciones, mi_reaccion: tipo };
        }
      }));
    } catch (err) {
      console.error('Error con reacción:', err);
    } finally {
      setReactingPost(null);
    }
  };

  // --- Topic Actions ---
  const crearTema = async () => {
    if (!topicForm.nombre.trim()) return;
    try {
      await axios.post(`${API_URL}/campus/espacios/${spaceId}/temas`, topicForm, { headers });
      setShowTopicForm(false);
      setTopicForm({ nombre: '', descripcion: '' });
      fetchTemas();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear tema');
    }
  };

  const eliminarTema = async (topicId) => {
    if (!window.confirm('¿Eliminar este tema y todos sus materiales?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/temas/${topicId}`, { headers });
      fetchTemas();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  // --- Material Actions ---
  const crearMaterial = async (topicId) => {
    if (!materialForm.titulo.trim()) return;
    try {
      await axios.post(`${API_URL}/campus/espacios/${spaceId}/materiales`,
        { ...materialForm, topic_id: topicId }, { headers }
      );
      setShowMaterialForm(null);
      setMaterialForm({ titulo: '', descripcion: '', tipo: 'link', url_externa: '' });
      fetchTemas();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al agregar material');
    }
  };

  const subirArchivoMaterial = async (topicId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('topic_id', topicId);
    formData.append('titulo', file.name);
    formData.append('descripcion', '');
    try {
      await axios.post(`${API_URL}/campus/espacios/${spaceId}/materiales/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchTemas();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al subir archivo');
    }
  };

  const eliminarMaterial = async (materialId) => {
    if (!window.confirm('¿Eliminar este material?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/materiales/${materialId}`, { headers });
      fetchTemas();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const copiarCodigo = () => {
    if (espacio?.codigo_acceso) {
      navigator.clipboard.writeText(espacio.codigo_acceso);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  };

  const formatFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatFechaCorta = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `hace ${diffH}h`;
    return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/campus')}>Volver al Campus</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const esCurso = espacio?.tipo === 'curso';

  const tabs = [
    { id: 'stream', label: 'Novedades', icon: MessageSquare },
    ...(esCurso ? [{ id: 'tareas', label: 'Tareas', icon: ClipboardList }] : []),
    ...(esCurso ? [{ id: 'examenes', label: 'Exámenes', icon: HelpCircle }] : []),
    { id: 'reuniones', label: 'Reuniones', icon: Video },
    { id: 'materiales', label: 'Materiales', icon: FolderOpen },
    { id: 'miembros', label: 'Miembros', icon: Users },
    ...(esCoordinadorODocente ? [{ id: 'config', label: 'Config', icon: Settings }] : []),
  ];

  // Context bundle passed to CampusPostCard (state + handlers)
  const postCtx = {
    user,
    spaceId,
    token,
    esCoordinadorODocente,
    formatFecha,
    expandedComments,
    commentsData,
    loadingComments,
    commentInputs,
    sendingComment,
    replyingTo,
    setReplyingTo,
    setCommentInputs,
    setCommentMenciones,
    pollSelection,
    showVoters,
    setShowVoters,
    votingPost,
    reactingPost,
    showReactionPicker,
    setShowReactionPicker,
    togglePollSelection,
    votarEncuesta,
    abrirEdicionEncuesta,
    cerrarEncuesta,
    iniciarEdicionPost,
    eliminarPost,
    toggleComments,
    toggleReaction,
    enviarComentario,
    editarComentario,
    eliminarComentario,
    reaccionarComentario,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />
      {/* Header */}
      <div style={{ backgroundColor: espacio?.color_tema || '#7c3aed' }} className="text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/campus')}
            className="text-white/80 hover:text-white hover:bg-white/10 mb-3"
            data-testid="back-to-campus"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Campus
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                {espacio?.tipo === 'curso'
                  ? <BookOpen className="w-6 h-6" />
                  : <Briefcase className="w-6 h-6" />
                }
                <h1 className="text-2xl font-bold" data-testid="space-title">{espacio?.nombre}</h1>
              </div>
              {espacio?.descripcion && (
                <p className="text-white/80 text-sm mt-1 max-w-2xl">{espacio.descripcion}</p>
              )}
              <div className="flex items-center gap-3 mt-2 text-white/70 text-xs">
                <Badge className="bg-white/20 text-white border-0 capitalize">{espacio?.tipo}</Badge>
                <span>{espacio?.total_miembros} miembros</span>
                <span className="capitalize">{espacio?.modalidad}</span>
              </div>
            </div>
            {esCoordinadorODocente && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/80 hover:text-white hover:bg-white/10"
                  onClick={() => navigate(`/campus/espacio/${spaceId}/metricas`)}
                  data-testid="ver-metricas-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-1" /> Métricas
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/80 hover:text-white hover:bg-white/10"
                  onClick={copiarCodigo}
                  data-testid="copy-code-btn"
                >
                  {copiado ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copiado ? 'Copiado' : espacio?.codigo_acceso}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto">
          <div
            className="flex gap-1 overflow-x-auto scrollbar-thin px-4"
            style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}
            data-testid="campus-tabs-scroll"
          >
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-violet-600 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* === TAB: STREAM === */}
        {activeTab === 'stream' && (
          <div className="space-y-4">
            <MiEstadoCurso spaceId={spaceId} esStaff={esCoordinadorODocente} />
            {/* Post creation form */}
            {esCoordinadorODocente && (
              <div>
                {!showPostForm ? (
                  <Card
                    className="cursor-pointer hover:shadow-sm transition-shadow"
                    onClick={() => setShowPostForm(true)}
                  >
                    <CardContent className="p-4 flex items-center gap-3 text-gray-400">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <Edit className="w-5 h-5" />
                      </div>
                      <span>Publicar algo al espacio...</span>
                    </CardContent>
                  </Card>
                ) : (
                  <Card data-testid="post-form">
                    <CardContent className="p-4 space-y-3">
                      <Input
                        placeholder={postForm.tipo === 'encuesta' ? 'Pregunta de la encuesta' : 'Titulo de la publicacion'}
                        value={postForm.titulo}
                        onChange={(e) => setPostForm({ ...postForm, titulo: e.target.value })}
                        data-testid="post-title-input"
                      />
                      {postForm.tipo !== 'encuesta' && (
                        <textarea
                          className="w-full border rounded-md px-3 py-2 text-sm min-h-[100px] resize-y"
                          placeholder="Escribi tu publicacion..."
                          value={postForm.contenido}
                          onChange={(e) => setPostForm({ ...postForm, contenido: e.target.value })}
                          data-testid="post-content-input"
                        />
                      )}

                      {/* Bloque de encuesta */}
                      {postForm.tipo === 'encuesta' && (
                        <div className="space-y-2 border border-violet-200 bg-violet-50/50 rounded-md p-3" data-testid="encuesta-form">
                          <div className="flex items-center gap-2 text-sm font-medium text-violet-700">
                            <BarChart2 className="w-4 h-4" /> Encuesta
                          </div>
                          {(postForm.encuesta.opciones || []).map((op, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-5">{idx + 1}.</span>
                              <Input
                                placeholder={`Opción ${idx + 1}`}
                                value={op}
                                onChange={(e) => {
                                  const nuevas = [...postForm.encuesta.opciones];
                                  nuevas[idx] = e.target.value;
                                  setPostForm({ ...postForm, encuesta: { ...postForm.encuesta, opciones: nuevas } });
                                }}
                                data-testid={`encuesta-opcion-${idx}`}
                              />
                              {postForm.encuesta.opciones.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const nuevas = postForm.encuesta.opciones.filter((_, i) => i !== idx);
                                    setPostForm({ ...postForm, encuesta: { ...postForm.encuesta, opciones: nuevas } });
                                  }}
                                  className="text-gray-400 hover:text-red-500 p-1"
                                  title="Quitar opción"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          {postForm.encuesta.opciones.length < 10 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setPostForm({
                                ...postForm,
                                encuesta: { ...postForm.encuesta, opciones: [...postForm.encuesta.opciones, ''] }
                              })}
                              className="text-violet-600 h-7"
                              data-testid="encuesta-add-opcion"
                            >
                              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar opción
                            </Button>
                          )}
                          <div className="flex flex-wrap gap-4 pt-2 text-sm border-t border-violet-200 mt-2">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={postForm.encuesta.multi}
                                onChange={(e) => setPostForm({
                                  ...postForm,
                                  encuesta: { ...postForm.encuesta, multi: e.target.checked }
                                })}
                                data-testid="encuesta-multi"
                              />
                              <span>Permitir múltiples respuestas</span>
                            </label>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Eye className="w-3.5 h-3.5" /> Todes pueden ver quién votó cada opción
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Lock className="w-3.5 h-3.5 text-gray-500" />
                            <span className="text-gray-600">Cierre opcional:</span>
                            <input
                              type="datetime-local"
                              className="border rounded px-2 py-1 text-sm"
                              value={postForm.encuesta.cierre || ''}
                              onChange={(e) => setPostForm({
                                ...postForm,
                                encuesta: { ...postForm.encuesta, cierre: e.target.value }
                              })}
                              data-testid="encuesta-cierre"
                            />
                            {postForm.encuesta.cierre && (
                              <button
                                type="button"
                                onClick={() => setPostForm({
                                  ...postForm,
                                  encuesta: { ...postForm.encuesta, cierre: '' }
                                })}
                                className="text-gray-400 hover:text-red-500"
                                title="Quitar cierre"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <select
                          className="border rounded px-2 py-1 text-sm"
                          value={postForm.tipo}
                          onChange={(e) => setPostForm({ ...postForm, tipo: e.target.value })}
                          data-testid="post-tipo-select"
                        >
                          <option value="anuncio">Anuncio</option>
                          <option value="material">Material</option>
                          <option value="convocatoria">Convocatoria</option>
                          <option value="acta">Acta</option>
                          <option value="recordatorio">Recordatorio</option>
                          {esCoordinadorODocente && <option value="encuesta">📊 Encuesta</option>}
                        </select>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={cancelarEdicionPost}>Cancelar</Button>
                          <Button
                            size="sm"
                            onClick={editingPostId ? actualizarPost : crearPost}
                            disabled={
                              savingPost ||
                              !postForm.titulo.trim() ||
                              (postForm.tipo !== 'encuesta' && !postForm.contenido.trim())
                            }
                            data-testid="publish-post-btn"
                          >
                            {savingPost ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                            {editingPostId ? 'Guardar cambios' : 'Publicar'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Posts list */}
            {loadingPosts ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
              </div>
            ) : posts.length === 0 ? (
              <Card className="text-center py-8">
                <CardContent>
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Todavia no hay publicaciones en este espacio</p>
                </CardContent>
              </Card>
            ) : (
              posts.map(post => (
                <CampusPostCard key={post.id} post={post} ctx={postCtx} />
              ))
            )}
          </div>
        )}

        {/* === TAB: TAREAS === */}
        {activeTab === 'tareas' && (
          <CampusTareas
            spaceId={spaceId}
            esCoordinadorODocente={esCoordinadorODocente}
            token={token}
          />
        )}

        {/* === TAB: EXÁMENES === */}
        {activeTab === 'examenes' && (
          <CampusQuizzes
            spaceId={spaceId}
            esCoordinadorODocente={esCoordinadorODocente}
            token={token}
          />
        )}

        {/* === TAB: REUNIONES === */}
        {activeTab === 'reuniones' && (
          <CampusMeetings
            spaceId={spaceId}
            esCoordinadorODocente={esCoordinadorODocente}
            token={token}
            tipoEspacio={espacio?.tipo}
          />
        )}

        {/* === TAB: MATERIALES === */}
        {activeTab === 'materiales' && (
          <div className="space-y-4">
            {esCoordinadorODocente && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setShowTopicForm(!showTopicForm)} data-testid="add-topic-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Nuevo Tema
                </Button>
              </div>
            )}

            {showTopicForm && (
              <Card data-testid="topic-form">
                <CardContent className="p-4 space-y-3">
                  <Input
                    placeholder="Nombre del tema"
                    value={topicForm.nombre}
                    onChange={(e) => setTopicForm({ ...topicForm, nombre: e.target.value })}
                    data-testid="topic-name-input"
                  />
                  <Input
                    placeholder="Descripcion (opcional)"
                    value={topicForm.descripcion}
                    onChange={(e) => setTopicForm({ ...topicForm, descripcion: e.target.value })}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setShowTopicForm(false)}>Cancelar</Button>
                    <Button size="sm" onClick={crearTema} disabled={!topicForm.nombre.trim()} data-testid="save-topic-btn">
                      Crear Tema
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {loadingMateriales ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
              </div>
            ) : temas.length === 0 ? (
              <Card className="text-center py-8">
                <CardContent>
                  <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay temas creados todavia</p>
                </CardContent>
              </Card>
            ) : (
              temas.map(tema => {
                const temaMateriales = materiales.filter(m => m.topic_id === tema.id);
                const isExpanded = expandedTopics[tema.id] !== false;
                return (
                  <Card key={tema.id} data-testid={`topic-${tema.id}`}>
                    <CardContent className="p-0">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedTopics(prev => ({ ...prev, [tema.id]: !isExpanded }))}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <FolderOpen className="w-5 h-5 text-violet-600" />
                          <span className="font-medium">{tema.nombre}</span>
                          <span className="text-xs text-gray-400">({tema.total_materiales || temaMateriales.length})</span>
                        </div>
                        {esCoordinadorODocente && (
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowMaterialForm(showMaterialForm === tema.id ? null : tema.id)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => eliminarTema(tema.id)}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="border-t">
                          {showMaterialForm === tema.id && (
                            <div className="p-4 bg-gray-50 border-b space-y-3" data-testid="material-form">
                              <Input
                                placeholder="Titulo del material"
                                value={materialForm.titulo}
                                onChange={(e) => setMaterialForm({ ...materialForm, titulo: e.target.value })}
                                data-testid="material-title-input"
                              />
                              <div className="flex gap-2">
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  value={materialForm.tipo}
                                  onChange={(e) => setMaterialForm({ ...materialForm, tipo: e.target.value })}
                                >
                                  <option value="link">Link</option>
                                  <option value="documento">Documento</option>
                                  <option value="video">Video</option>
                                  <option value="archivo">Archivo</option>
                                </select>
                                <Input
                                  className="flex-1"
                                  placeholder="URL del recurso"
                                  value={materialForm.url_externa}
                                  onChange={(e) => setMaterialForm({ ...materialForm, url_externa: e.target.value })}
                                />
                              </div>
                              <div className="flex items-center gap-2 justify-between">
                                <label className="flex items-center gap-2 text-xs text-violet-600 cursor-pointer hover:text-violet-800 bg-violet-50 px-3 py-2 rounded-md border border-dashed border-violet-300">
                                  <Upload className="w-4 h-4" />
                                  <span>Subir archivo</span>
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) subirArchivoMaterial(tema.id, e.target.files[0]);
                                      e.target.value = '';
                                    }}
                                    data-testid="material-file-upload"
                                  />
                                </label>
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" onClick={() => setShowMaterialForm(null)}>Cancelar</Button>
                                  <Button size="sm" onClick={() => crearMaterial(tema.id)} disabled={!materialForm.titulo.trim()} data-testid="save-material-btn">
                                    Agregar link
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {temaMateriales.length === 0 ? (
                            <div className="p-4 text-sm text-gray-400 text-center">Sin materiales</div>
                          ) : (
                            temaMateriales.map(mat => (
                              <div key={mat.id} className="flex items-center justify-between px-4 py-3 border-b last:border-b-0 hover:bg-gray-50">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-4 h-4 text-gray-400" />
                                  <div>
                                    <span className="text-sm font-medium">{mat.titulo}</span>
                                    {mat.nombre_archivo && <p className="text-xs text-gray-400">{mat.nombre_archivo}</p>}
                                    {!mat.nombre_archivo && mat.descripcion && <p className="text-xs text-gray-400">{mat.descripcion}</p>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs capitalize">{mat.tipo}</Badge>
                                  {(mat.url_externa || mat.url_archivo) && (
                                    <a
                                      href={mat.url_externa || mat.url_archivo}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-violet-600 hover:text-violet-800"
                                    >
                                      <Link2 className="w-4 h-4" />
                                    </a>
                                  )}
                                  {esCoordinadorODocente && (
                                    <button onClick={() => eliminarMaterial(mat.id)} className="text-gray-400 hover:text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* === TAB: MIEMBROS === */}
        {activeTab === 'miembros' && (
          <div className="space-y-4">
            {esCoordinadorODocente && (
              <Card className="bg-violet-50 border-violet-200">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-violet-900">Codigo de invitacion</p>
                    <p className="font-mono text-lg font-bold text-violet-700">{espacio?.codigo_acceso}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={copiarCodigo} data-testid="copy-invite-code">
                      {copiado ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copiado ? 'Copiado' : 'Copiar'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowInviteEmail(!showInviteEmail); setShowAddMember(false); setInviteFeedback(null); }} data-testid="invite-email-btn">
                      <Send className="w-4 h-4 mr-1" />
                      Invitar por email
                    </Button>
                    <Button size="sm" onClick={() => { setShowAddMember(!showAddMember); setShowInviteEmail(false); }} data-testid="add-member-btn">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Invite by email panel */}
            {showInviteEmail && esCoordinadorODocente && (
              <Card data-testid="invite-email-panel">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Send className="w-4 h-4 text-violet-600" />
                      Invitar por email
                    </h3>
                    {espacio?.tipo === 'comision' && (
                      <Badge variant="outline" className="text-xs">Solo matriculades</Badge>
                    )}
                  </div>

                  {/* Toggle modo */}
                  <div className="inline-flex bg-gray-100 rounded-md p-0.5 text-xs">
                    <button
                      onClick={() => { setInviteMode('single'); setBulkResults(null); setInviteFeedback(null); }}
                      className={`px-3 py-1 rounded ${inviteMode === 'single' ? 'bg-white shadow text-violet-700 font-medium' : 'text-gray-600'}`}
                      data-testid="invite-mode-single"
                    >
                      Uno por uno
                    </button>
                    <button
                      onClick={() => { setInviteMode('bulk'); setBulkResults(null); setInviteFeedback(null); }}
                      className={`px-3 py-1 rounded ${inviteMode === 'bulk' ? 'bg-white shadow text-violet-700 font-medium' : 'text-gray-600'}`}
                      data-testid="invite-mode-bulk"
                    >
                      Carga masiva
                    </button>
                  </div>

                  <p className="text-xs text-gray-500">
                    {inviteMode === 'single'
                      ? 'Le llega un mail con un link. Si todavía no tiene cuenta, puede crearla en pocos pasos y se suma automáticamente al espacio.'
                      : 'Pegá hasta 100 emails (separados por coma, espacio o salto de línea). Vamos a procesarlos uno por uno y te mostramos el resultado.'}
                  </p>

                  {inviteMode === 'single' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input
                        placeholder="email@ejemplo.com"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="sm:col-span-2"
                        data-testid="invite-email-input"
                      />
                      <select
                        value={inviteRol}
                        onChange={(e) => setInviteRol(e.target.value)}
                        className="border rounded-md px-3 text-sm h-10 bg-white"
                        data-testid="invite-rol-select"
                      >
                        <option value="estudiante">Estudiante</option>
                        <option value="docente">Docente</option>
                        <option value="coordinador">Coordinador</option>
                        <option value="invitado">Invitado</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={inviteBulkText}
                        onChange={(e) => setInviteBulkText(e.target.value)}
                        rows={5}
                        placeholder={"maria@ejemplo.com\njuan@ejemplo.com\nlucia@ejemplo.com\n..."}
                        className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                        data-testid="invite-bulk-textarea"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">
                          {(() => {
                            const count = inviteBulkText.split(/[\s,;]+/).filter(Boolean).length;
                            return count > 0 ? `${count} ${count === 1 ? 'email detectado' : 'emails detectados'}` : 'Sin emails todavía';
                          })()}
                        </span>
                        <select
                          value={inviteRol}
                          onChange={(e) => setInviteRol(e.target.value)}
                          className="border rounded-md px-3 text-sm h-9 bg-white"
                          data-testid="invite-rol-bulk-select"
                        >
                          <option value="estudiante">Estudiante</option>
                          <option value="docente">Docente</option>
                          <option value="coordinador">Coordinador</option>
                          <option value="invitado">Invitado</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <Input
                    placeholder="Mensaje personal (opcional)"
                    value={inviteMsg}
                    onChange={(e) => setInviteMsg(e.target.value)}
                    maxLength={300}
                    data-testid="invite-msg-input"
                  />

                  {inviteFeedback && (
                    <div
                      className={`text-sm rounded-md px-3 py-2 ${
                        inviteFeedback.warning
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : inviteFeedback.ok
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                      data-testid="invite-feedback"
                    >
                      {inviteFeedback.message}
                    </div>
                  )}

                  {/* Resultados de carga masiva */}
                  {bulkResults && (
                    <div className="border rounded-md max-h-72 overflow-y-auto bg-white" data-testid="bulk-results">
                      <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-700 sticky top-0">
                        Detalle por email
                      </div>
                      <div className="divide-y text-sm">
                        {bulkResults.invalidos?.map((em, i) => (
                          <div key={`inv-${i}`} className="flex items-center justify-between px-3 py-1.5 bg-red-50">
                            <span className="font-mono text-xs truncate flex-1">{em}</span>
                            <Badge variant="outline" className="text-xs text-red-600 border-red-300">
                              Formato inválido
                            </Badge>
                          </div>
                        ))}
                        {bulkResults.resultados.map((r) => (
                          <div
                            key={r.email}
                            className={`flex items-center justify-between px-3 py-1.5 ${
                              r.success ? '' : 'bg-red-50'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs truncate">{r.email}</div>
                              <div className="text-xs text-gray-500 truncate">{r.message}</div>
                            </div>
                            <Badge
                              variant="outline"
                              className={`text-xs ml-2 ${
                                r.tipo === 'miembro_existente'
                                  ? 'text-blue-700 border-blue-300'
                                  : r.tipo === 'invitacion_pendiente'
                                    ? 'text-violet-700 border-violet-300'
                                    : r.success
                                      ? 'text-green-700 border-green-300'
                                      : 'text-red-700 border-red-300'
                              }`}
                            >
                              {r.tipo === 'miembro_existente'
                                ? 'Sumade'
                                : r.tipo === 'invitacion_pendiente'
                                  ? 'Invitada'
                                  : r.tipo === 'miembro_existente_sin_email' || r.tipo === 'invitacion_creada_sin_email'
                                    ? 'OK (sin email)'
                                    : r.success ? 'OK' : 'Error'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowInviteEmail(false); setInviteFeedback(null); setBulkResults(null); }}
                    >
                      Cerrar
                    </Button>
                    {inviteMode === 'single' ? (
                      <Button
                        size="sm"
                        onClick={enviarInvitacionEmail}
                        disabled={invitingEmail || !inviteEmail.trim()}
                        data-testid="enviar-invitacion-btn"
                      >
                        {invitingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Enviar invitación
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={enviarInvitacionMasiva}
                        disabled={invitingEmail || !inviteBulkText.trim()}
                        data-testid="enviar-invitacion-masiva-btn"
                      >
                        {invitingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Enviar invitaciones
                      </Button>
                    )}
                  </div>

                  {/* Pending invitations list */}
                  {invitacionesPendientes.length > 0 && (
                    <div className="border-t pt-3 mt-2">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">
                        Invitaciones pendientes ({invitacionesPendientes.length})
                      </h4>
                      <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                        {invitacionesPendientes.map((inv) => (
                          <div
                            key={inv.id}
                            className="flex items-center justify-between gap-2 bg-gray-50 rounded-md px-3 py-2 text-sm"
                            data-testid={`invitacion-${inv.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{inv.email}</div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{inv.rol}</span>
                                <span>·</span>
                                {inv.estado === 'expirada' ? (
                                  <Badge variant="outline" className="text-xs text-red-600 border-red-200">Expirada</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">Pendiente</Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => reenviarInvitacion(inv.id)}
                              disabled={reenviandoInv === inv.id}
                              data-testid={`reenviar-inv-${inv.id}`}
                              title="Reenviar email"
                            >
                              {reenviandoInv === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => cancelarInvitacion(inv.id, inv.email)}
                              disabled={cancelandoInv === inv.id}
                              data-testid={`cancelar-inv-${inv.id}`}
                              title="Cancelar invitación"
                            >
                              {cancelandoInv === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {loadingInvitaciones && invitacionesPendientes.length === 0 && (
                    <div className="flex justify-center py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Add member search */}
            {showAddMember && esCoordinadorODocente && (
              <Card data-testid="add-member-panel">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Agregar participante</h3>
                    {espacio?.tipo === 'comision' && (
                      <Badge variant="outline" className="text-xs">Solo matriculados</Badge>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por nombre, apellido, email o matrícula..."
                      value={searchQuery}
                      onChange={(e) => buscarUsuarios(e.target.value)}
                      className="pl-10"
                      data-testid="search-user-input"
                    />
                  </div>
                  {searching && (
                    <div className="flex justify-center py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                    </div>
                  )}
                  {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">No se encontraron usuarios</p>
                  )}
                  {searchResults.length > 0 && (
                    <div className="border rounded-md divide-y max-h-[240px] overflow-y-auto">
                      {searchResults.map(u => (
                        <div key={u.user_id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50"
                          data-testid={`search-result-${u.user_id}`}>
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 font-semibold text-xs">
                              {(u.nombre?.[0] || '?').toUpperCase()}
                            </div>
                            <div>
                              <span className="text-sm font-medium">{u.nombre} {u.apellido}</span>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                {u.email && <span>{u.email}</span>}
                                {u.matricula && <span>Mat. {u.matricula}</span>}
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => agregarMiembro(u)}
                            disabled={addingUser === u.user_id}
                            className="h-7 text-xs"
                            data-testid={`add-user-${u.user_id}`}
                          >
                            {addingUser === u.user_id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><UserPlus className="w-3.5 h-3.5 mr-1" />Agregar</>
                            }
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notification toggle */}
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {notifEnabled ? <Bell className="w-5 h-5 text-violet-600" /> : <BellOff className="w-5 h-5 text-gray-400" />}
                  <div>
                    <p className="text-sm font-medium">Notificaciones por email</p>
                    <p className="text-xs text-gray-500">
                      {notifEnabled ? 'Recibís emails cuando hay novedades' : 'No recibís emails de este espacio'}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={notifEnabled ? 'outline' : 'default'}
                  onClick={toggleNotif}
                  disabled={togglingNotif}
                  data-testid="toggle-notifications-btn"
                >
                  {togglingNotif
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : notifEnabled ? 'Desactivar' : 'Activar'
                  }
                </Button>
              </CardContent>
            </Card>

            {loadingMiembros ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
              </div>
            ) : (() => {
              const q = miembrosQuery.trim().toLowerCase();
              const miembrosFiltrados = q
                ? miembros.filter((m) => {
                    const nombre = (m.nombre || '').toLowerCase();
                    const apellido = (m.apellido || '').toLowerCase();
                    const email = (m.email || '').toLowerCase();
                    const nombreCompleto = `${nombre} ${apellido}`.trim();
                    return (
                      nombre.includes(q) ||
                      apellido.includes(q) ||
                      nombreCompleto.includes(q) ||
                      email.includes(q)
                    );
                  })
                : miembros;
              return (
              <Card>
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Miembros ({miembros.length}
                      {q && miembrosFiltrados.length !== miembros.length
                        ? ` · ${miembrosFiltrados.length} coinciden`
                        : ''})
                    </CardTitle>
                    {esCoordinadorODocente && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportarEmailsMiembros(miembrosFiltrados)}
                        disabled={miembrosFiltrados.filter((m) => m.email).length === 0}
                        data-testid="miembros-exportar-emails"
                      >
                        <Download className="w-4 h-4 mr-1.5" />
                        Exportar emails{q ? ' (filtrados)' : ''}
                      </Button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <Input
                      value={miembrosQuery}
                      onChange={(e) => setMiembrosQuery(e.target.value)}
                      placeholder="Buscar por nombre, apellido o email…"
                      className="pl-9"
                      data-testid="miembros-search-input"
                    />
                    {miembrosQuery && (
                      <button
                        type="button"
                        onClick={() => setMiembrosQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        data-testid="miembros-search-clear"
                        aria-label="Limpiar búsqueda"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {miembros.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">Sin miembros</div>
                  ) : miembrosFiltrados.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm" data-testid="miembros-search-empty">
                      No hay miembros que coincidan con "{miembrosQuery}".
                    </div>
                  ) : (
                    <div className="divide-y">
                      {miembrosFiltrados.map((m, i) => {
                        const esSelf = m.user_id === user?.id;
                        const isEditingRole = changingRole === m.user_id;
                        const esCoord = espacio?.mi_rol === 'coordinador';
                        return (
                          <div key={m.id || i} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 group"
                            data-testid={`member-row-${m.user_id || i}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 font-semibold text-sm">
                                {(m.nombre?.[0] || '?').toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm font-medium">{m.nombre} {m.apellido}</span>
                                {m.email && <p className="text-xs text-gray-400">{m.email}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isEditingRole ? (
                                <div className="flex items-center gap-1" data-testid={`role-selector-${m.user_id}`}>
                                  {['coordinador', 'docente', 'estudiante'].map(rol => (
                                    <button
                                      key={rol}
                                      onClick={() => cambiarRol(m.user_id, rol)}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                        m.rol === rol
                                          ? 'bg-violet-600 text-white'
                                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                      }`}
                                      data-testid={`role-option-${rol}-${m.user_id}`}
                                    >
                                      {rol.charAt(0).toUpperCase() + rol.slice(1)}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => setChangingRole(null)}
                                    className="text-gray-400 hover:text-gray-600 ml-1"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <Badge
                                    variant={m.rol === 'coordinador' ? 'default' : m.rol === 'docente' ? 'secondary' : 'outline'}
                                    className={`text-xs capitalize ${esCoord && !esSelf ? 'cursor-pointer hover:ring-2 hover:ring-violet-200' : ''}`}
                                    onClick={() => esCoord && !esSelf && setChangingRole(m.user_id)}
                                    data-testid={`role-badge-${m.user_id || i}`}
                                  >
                                    {m.rol}
                                  </Badge>
                                  {esCoord && !esSelf && (
                                    <button
                                      onClick={() => removerMiembro(m.user_id, `${m.nombre} ${m.apellido}`)}
                                      className="text-gray-300 hover:text-red-500 p-0.5 opacity-0 group-hover:opacity-100"
                                      title="Remover del espacio"
                                      data-testid={`remove-member-${m.user_id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })()}
          </div>
        )}

        {/* === TAB: CONFIG (solo coordinador/docente) === */}
        {activeTab === 'config' && esCoordinadorODocente && (
          <div className="space-y-4">
            <CampusCursoVinculadoConfig spaceId={spaceId} />
            <CampusNotifConfig spaceId={spaceId} />
          </div>
        )}
      </div>

      {/* Click outside to close reaction picker */}
      {showReactionPicker && (
        <div className="fixed inset-0 z-10" onClick={() => setShowReactionPicker(null)} />
      )}

      {/* Modal de edición de encuesta */}
      {editingPoll && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => !savingEditPoll && setEditingPoll(null)}
          data-testid="edit-poll-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold">Editar encuesta</h3>
              </div>
              <button
                onClick={() => setEditingPoll(null)}
                disabled={savingEditPoll}
                className="text-gray-400 hover:text-gray-600"
                data-testid="edit-poll-cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase mb-1 block">Consigna</label>
                <Input
                  value={editPollForm.titulo}
                  onChange={(e) => setEditPollForm({ ...editPollForm, titulo: e.target.value })}
                  placeholder="Pregunta de la encuesta"
                  data-testid="edit-poll-titulo"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase mb-1 block">Opciones</label>
                <div className="space-y-2">
                  {editPollForm.opciones.map((op, idx) => (
                    <div key={op.id || `new-${idx}`} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-5">{idx + 1}.</span>
                      <Input
                        value={op.texto}
                        onChange={(e) => {
                          const nuevas = [...editPollForm.opciones];
                          nuevas[idx] = { ...nuevas[idx], texto: e.target.value };
                          setEditPollForm({ ...editPollForm, opciones: nuevas });
                        }}
                        placeholder={`Opción ${idx + 1}`}
                        data-testid={`edit-poll-opcion-${idx}`}
                      />
                      {editPollForm.opciones.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const nuevas = editPollForm.opciones.filter((_, i) => i !== idx);
                            setEditPollForm({ ...editPollForm, opciones: nuevas });
                          }}
                          className="text-gray-400 hover:text-red-500 p-1"
                          title="Quitar opción (se borrarán los votos a esta opción)"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {editPollForm.opciones.length < 10 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditPollForm({
                        ...editPollForm,
                        opciones: [...editPollForm.opciones, { texto: '' }]
                      })}
                      className="text-violet-600 h-7"
                      data-testid="edit-poll-add-opcion"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Agregar opción
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  💡 Las opciones existentes conservan los votos al renombrarlas. Si las eliminás, también se borran sus votos.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 pt-2 text-sm border-t">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editPollForm.multi}
                    onChange={(e) => setEditPollForm({ ...editPollForm, multi: e.target.checked })}
                    data-testid="edit-poll-multi"
                  />
                  <span>Permitir múltiples respuestas</span>
                </label>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Lock className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-gray-600">Cierre opcional:</span>
                <input
                  type="datetime-local"
                  className="border rounded px-2 py-1 text-sm"
                  value={editPollForm.cierre}
                  onChange={(e) => setEditPollForm({ ...editPollForm, cierre: e.target.value })}
                  data-testid="edit-poll-cierre"
                />
                {editPollForm.cierre && (
                  <button
                    type="button"
                    onClick={() => setEditPollForm({ ...editPollForm, cierre: '' })}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="border-t px-4 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingPoll(null)} disabled={savingEditPoll}>
                Cancelar
              </Button>
              <Button
                onClick={guardarEdicionEncuesta}
                disabled={savingEditPoll}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="edit-poll-guardar"
              >
                {savingEditPoll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                Guardar cambios
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
